import { useDispatch, useSelector } from "react-redux";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { setClearHomepageVideos } from "../../redux/ApiSlice/HomePageVideoSlice";
import { deleteAccount, clearDeleteAccountError } from "../../redux/ApiSlice/AccountSettingSlice";
import { clearAccountProfile } from "../../redux/ApiSlice/AccountProfileSlice";
import { persistor } from "../../redux/store";
import { useState, useEffect, useRef, useCallback } from "react";
import { IoClose, IoChevronDown } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import { logoutUser } from "../../redux/ApiSlice/LogoutSlice";
import { logout } from "../../redux/ApiSlice/Auth/AuthSlice";

const DELETION_REASONS = [
    { value: "Privacy concerns", label: "Privacy concerns" },
    { value: "Too many notifications", label: "Too many notifications" },
    { value: "Not useful anymore", label: "Not useful anymore" },
    { value: "Found alternative", label: "Found alternative" },
    { value: "Temporary break", label: "Temporary break" },
    { value: "Other", label: "Other" }
];

const AccountDelete = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [password, setPassword] = useState("");
    const [deletionReason, setDeletionReason] = useState("");
    const [errors, setErrors] = useState({});
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const timerRef = useRef(null);

    const deleteAccountLoading = useSelector((state) => state.AccountSetting.deleteAccountLoading);
    const deleteAccountError = useSelector((state) => state.AccountSetting.deleteAccountError);
    const deleteAccountSuccess = useSelector((state) => state.AccountSetting.deleteAccountSuccess);

    const handleLogOut = useCallback(async () => {
        await persistor.flush();
        await persistor.purge();
        dispatch(clearAccountProfile());
        dispatch(setClearHomepageVideos());
        await dispatch(logoutUser());
        await dispatch(logout());
        localStorage.clear();
        timerRef.current = setTimeout(() => {
            navigate("/login");
        }, 500);
    }, [dispatch, navigate]);

    const handleClosePopup = useCallback(() => {
        setShowDeletePopup(false);
        setPassword("");
        setDeletionReason("");
        setErrors({});
        setIsDropdownOpen(false);
        dispatch(clearDeleteAccountError());
    }, [dispatch]);

    const handleDeleteAccount = useCallback(() => {
        setShowDeletePopup(true);
        setErrors({});
    }, []);

    const validateForm = useCallback(() => {
        const newErrors = {};
        if (!password.trim()) newErrors.password = "Password is required";
        if (!deletionReason) newErrors.deletionReason = "Deletion reason is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [password, deletionReason]);

    const handleSubmitDelete = useCallback(() => {
        if (validateForm()) {
            dispatch(deleteAccount({ password, deletion_reason: deletionReason }));
        }
    }, [validateForm, dispatch, password, deletionReason]);

    const handleSelectReason = useCallback((value) => {
        setDeletionReason(value);
        setIsDropdownOpen(false);
    }, []);

    useEffect(() => {
        if (deleteAccountSuccess) {
            handleClosePopup();
            handleLogOut();
        }
    }, [deleteAccountSuccess, handleClosePopup, handleLogOut]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDropdownOpen]);
    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    return (
        <div className="bg-white dark:bg-[#1E1D21]">
            <div className="sticky top-0 z-50">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                    <IoMdArrowBack
                        onClick={() => navigate("/settings/account")}
                        className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                    />
                    <p className="text-center font-bold text-[16px] text-black flex-grow">Delete Your Account</p>
                </div>
            </div>

            <div className="px-3 flex flex-col justify-between h-[calc(80vh-60px)]">
                <div className="flex flex-col pt-5">
                    <p className="text-[14px] text-black dark:text-white font-bold">Delete your Account</p>
                    <p className="text-[12px] text-black dark:text-white pt-1">
                        If you choose to delete your account, you'll be asked to confirm your decision.
                        Deleting your account will permanently remove your content and associated data,
                        in accordance with GDPR.
                    </p>
                    <p className="text-[12px] text-black dark:text-white pt-2">
                        Before deletion, your account will be deactivated for 14 days. During this period, you can simply
                        log back in to cancel the deletion. On day 14, we'll send you a reminder email—if you still wish
                        to proceed, no action is needed.
                    </p>
                    <p className="text-[12px] text-black dark:text-white pt-2">
                        After your account is deleted, you'll need to create a new one if you'd like to return to
                        Chaa Chingg. We recommend downloading your profile data beforehand so you have a copy of your information.
                    </p>
                </div>

                <div className="flex flex-col gap-3">
                    <button
                        onClick={() => navigate("/settings/account/profile-data")}
                        className="text-[13px] w-full border-2 border-[#BDE6FF] dark:border-[#333333] bg-white dark:bg-[#333333] text-[#333333] dark:text-white font-bold py-3 rounded-lg"
                    >
                        Request profile data
                    </button>

                    <button
                        onClick={handleDeleteAccount}
                        className="text-[13px] w-full bg-[#BDE6FF] text-black font-bold py-3 rounded-lg"
                    >
                        Delete your account
                    </button>
                </div>
            </div>

            {showDeletePopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
                    <div className="bg-white dark:bg-[#333333] rounded-lg w-full max-w-md p-6 relative">
                        <IoClose
                            onClick={handleClosePopup}
                            className="absolute right-4 top-4 text-2xl text-black dark:text-white cursor-pointer"
                        />

                        <h2 className="text-[18px] font-bold text-black dark:text-white mb-4">
                            Delete Your Account
                        </h2>

                        <p className="text-[12px] text-gray-600 dark:text-gray-300 mb-4">
                            Please confirm your password and provide a reason for deleting your account.
                        </p>

                        {deleteAccountError && (
                            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded-lg text-[12px]">
                                {typeof deleteAccountError === 'string'
                                    ? deleteAccountError
                                    : deleteAccountError.detail || deleteAccountError.message || "Failed to delete account"}
                            </div>
                        )}

                        <div className="space-y-4">
                            <div className="relative" ref={dropdownRef}>
                                <label className="block text-[13px] font-semibold text-black dark:text-white mb-2">
                                    Reason for Deletion *
                                </label>
                                <div
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[13px] text-black dark:text-white bg-white dark:bg-[#333333] focus:outline-none cursor-pointer flex items-center justify-between"
                                >
                                    <span className={deletionReason ? "text-black dark:text-white" : "text-gray-400"}>
                                        {deletionReason || "Select a reason"}
                                    </span>
                                    <IoChevronDown
                                        className={`text-lg transition-transform duration-300 ${isDropdownOpen ? "rotate-180" : "rotate-0"}`}
                                    />
                                </div>

                                <AnimatePresence>
                                    {isDropdownOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.2 }}
                                            className="h-[10rem] absolute z-10 w-full mt-1 bg-white dark:bg-[#333333] border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg overflow-y-auto"
                                        >
                                            {DELETION_REASONS.map((reason) => (
                                                <div
                                                    key={reason.value}
                                                    onClick={() => handleSelectReason(reason.value)}
                                                    className={`px-3 py-2.5 text-[13px] cursor-pointer transition-colors ${deletionReason === reason.value
                                                        ? "bg-[#BDE6FF] text-black font-semibold"
                                                        : "text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600"
                                                        }`}
                                                >
                                                    {reason.label}
                                                </div>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {errors.deletionReason && (
                                    <p className="text-red-500 text-[11px] mt-1">{errors.deletionReason}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-[13px] font-semibold text-black dark:text-white mb-2">
                                    Password *
                                </label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[13px] text-black dark:text-white bg-white dark:bg-[#333333] focus:outline-none focus:ring-2 focus:ring-[#BDE6FF]"
                                />
                                {errors.password && (
                                    <p className="text-red-500 text-[11px] mt-1">{errors.password}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={handleClosePopup}
                                disabled={deleteAccountLoading}
                                className="flex-1 text-[13px] border-2 border-[#BDE6FF] dark:border-[#555555] bg-white dark:bg-[#333333] text-black dark:text-white font-bold py-3 rounded-lg disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmitDelete}
                                disabled={deleteAccountLoading}
                                className="flex-1 text-[13px] bg-red-500 text-white font-bold py-3 rounded-lg disabled:opacity-50 flex items-center justify-center"
                            >
                                {deleteAccountLoading ? "Deleting..." : "Confirm Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountDelete;
